import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { SimBanner } from "@/components/SimBanner";
import { EmployeeNav } from "@/components/EmployeeNav";
import { AdminSidebar } from "@/components/AdminSidebar";

import LoginPage from "@/pages/LoginPage";
import StatusPage from "@/pages/StatusPage";
import EmployeeHome from "@/pages/employee/EmployeeHome";
import EmployeeRekap from "@/pages/employee/EmployeeRekap";
import EmployeeKasbon from "@/pages/employee/EmployeeKasbon";
import EmployeePayslip from "@/pages/employee/EmployeePayslip";
import EmployeePerformance from "@/pages/employee/EmployeePerformance";
import EmployeeLoans from "@/pages/employee/EmployeeLoans";
import EmployeePhlWallet from "@/pages/employee/EmployeePhlWallet";
import EmployeeProfile from "@/pages/employee/EmployeeProfile";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminEmployees from "@/pages/admin/AdminEmployees";
import AdminAbsen from "@/pages/admin/AdminAbsen";
import AdminRekap from "@/pages/admin/AdminRekap";
import AdminKasbon from "@/pages/admin/AdminKasbon";
import AdminLoans from "@/pages/admin/AdminLoans";
import AdminPhlPayouts from "@/pages/admin/AdminPhlPayouts";
import AdminFreePeriods from "@/pages/admin/AdminFreePeriods";
import AdminPerformance from "@/pages/admin/AdminPerformance";
import AdminPerformanceDetail from "@/pages/admin/AdminPerformanceDetail";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminLeave from "@/pages/admin/AdminLeave";
import AdminMultiplier from "@/pages/admin/AdminMultiplier";
import AdminLocations from "@/pages/admin/AdminLocations";
import AdminSimulasi from "@/pages/admin/AdminSimulasi";
import AdminPayslip from "@/pages/admin/AdminPayslip";

const ProtectedRoute = ({ role }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-center text-sm text-zinc-500">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/pegawai"} replace />;
  return <Outlet />;
};

const NonHourlyOnly = () => {
  const { user } = useAuth();
  if (user?.pay_type === "hourly") return <Navigate to="/pegawai" replace />;
  return <Outlet />;
};

const HourlyOnly = () => {
  const { user } = useAuth();
  if (user?.pay_type !== "hourly") return <Navigate to="/pegawai" replace />;
  return <Outlet />;
};

const EmployeeLayout = () => (
  <div className="min-h-screen bg-white">
    <SimBanner />
    <main className="pb-safe">
      <Outlet />
    </main>
    <EmployeeNav />
  </div>
);

const AdminLayout = () => (
  <div className="min-h-screen bg-zinc-50">
    <SimBanner />
    <div className="flex flex-col lg:flex-row">
      <AdminSidebar />
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  </div>
);

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-center text-sm text-zinc-500">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/pegawai"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route element={<ProtectedRoute role="pegawai" />}>
            <Route element={<EmployeeLayout />}>
              <Route path="/pegawai" element={<EmployeeHome />} />
              <Route path="/pegawai/rekap" element={<EmployeeRekap />} />
              <Route element={<NonHourlyOnly />}>
                <Route path="/pegawai/kasbon" element={<EmployeeKasbon />} />
                <Route path="/pegawai/slip" element={<EmployeePayslip />} />
                <Route path="/pegawai/pinjaman" element={<EmployeeLoans />} />
                <Route path="/pegawai/performa" element={<EmployeePerformance />} />
              </Route>
              <Route element={<HourlyOnly />}>
                <Route path="/pegawai/upah" element={<EmployeePhlWallet />} />
              </Route>
              <Route path="/pegawai/profil" element={<EmployeeProfile />} />
            </Route>
          </Route>
          <Route element={<ProtectedRoute role="admin" />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/pegawai" element={<AdminEmployees />} />
              <Route path="/admin/absen" element={<AdminAbsen />} />
              <Route path="/admin/rekap" element={<AdminRekap />} />
              <Route path="/admin/kasbon" element={<AdminKasbon />} />
              <Route path="/admin/pinjaman" element={<AdminLoans />} />
              <Route path="/admin/upah-phl" element={<AdminPhlPayouts />} />
              <Route path="/admin/performa" element={<AdminPerformance />} />
              <Route path="/admin/performa/:userId" element={<AdminPerformanceDetail />} />
              <Route path="/admin/periode-bebas" element={<AdminFreePeriods />} />
              <Route path="/admin/pengumuman" element={<AdminAnnouncements />} />
              <Route path="/admin/izin" element={<AdminLeave />} />
              <Route path="/admin/multiplier" element={<AdminMultiplier />} />
              <Route path="/admin/lokasi" element={<AdminLocations />} />
              <Route path="/admin/slip" element={<AdminPayslip />} />
              <Route path="/admin/simulasi" element={<AdminSimulasi />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
